import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Platform, Pressable, View, Text } from 'react-native';
import { Galeria } from '@nandorojo/galeria';
import { Image } from 'expo-image';
import { VideoView, useVideoPlayer } from 'expo-video';
import { useEvent } from 'expo';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import ImageContextWrapper from '@/components/General/ImageContextWrapper';

//Note: useUnistyles used for the error placeholder theme colors.

/**
 * MediaViewer — wraps a set of image thumbnails in galeria for pinch/zoom/swipe fullscreen.
 * Videos render inline via expo-video with fullscreen support.
 *
 * Props:
 *   media: [{ uri, thumbnailUri, type, width, height }]  (images + videos mixed)
 *   startIndex: number  (which image opens first when tapped — galeria handles via index on Galeria.Image)
 */
const MediaViewer = ({ media = [] }) => {
    //Guard against undefined/incomplete entries (attachments can resolve to holes while loading).
    const validMedia = useMemo(() => (media || []).filter(m => m && (m.uri || m.thumbnailUri)), [media]);
    const imageUrls = useMemo(() => validMedia.filter(m => m.type != 'video').map(m => m.uri || m.thumbnailUri), [validMedia]);

    if(validMedia.length == 0) return null;

    //Single galeria wrapping all images; videos rendered separately with their own player
    let imgIdx = -1;
    const imageElements = validMedia.map((m, i) => {
        if(m.type == 'video') return null;
        imgIdx++;
        return (
            <GaleriaImage key={`img-${i}`} index={imgIdx} media={m} />
        );
    }).filter(Boolean);

    const videoElements = validMedia.map((m, i) => {
        if(m.type != 'video') return null;
        return <InlineVideo key={`vid-${i}`} index={i} uri={m.uri} thumbnailUri={m.thumbnailUri} width={m.width} height={m.height} />;
    }).filter(Boolean);

    //If only images, wrap in single Galeria
    if(videoElements.length == 0 && imageElements.length > 0){
        return (
            <Galeria urls={imageUrls}>
                <View style={styles.mediaRow}>
                    {imageElements}
                </View>
            </Galeria>
        );
    }

    //Mixed: wrap images in galeria, render videos alongside
    return (
        <View style={styles.mediaRow}>
            {imageElements.length > 0 && (
                <Galeria urls={imageUrls}>
                    {imageElements}
                </Galeria>
            )}
            {videoElements}
        </View>
    );
};

const GaleriaImage = ({ index, media }) => {
    const { theme } = useUnistyles();
    const [errored, setErrored] = useState(false);
    const [measuredSize, setMeasuredSize] = useState(null);

    //The box must match the image's real aspect ratio — galeria's web lightbox reads the
    //rendered <img> bounding rect to size the fullscreen view, so a square box gets cropped.
    //When caller dims are missing, measure on load and rescale (max 250 like FileManager).
    const w = media.width || measuredSize?.width || 150;
    const h = media.height || measuredSize?.height || 150;

    const handleLoad = (e) => {
        const sw = e?.source?.width;
        const sh = e?.source?.height;
        if(!sw || !sh || (media.width && media.height)) return;
        const divisor = Math.max(sw, sh) / 250;
        const next = { width: sw / divisor, height: sh / divisor };
        setMeasuredSize(prev => (prev?.width == next.width && prev?.height == next.height) ? prev : next);
    };

    if(errored){
        return (
            <Galeria.Image index={index}>
                <View style={styles.errorPlaceholder(w,h)}>
                    <Unicon name="image-outline" size={32} colorVariant="textSecondary" />
                    <Text style={[styles.errorText, { color: theme.colors.textSecondary }]}>Failed to load</Text>
                </View>
            </Galeria.Image>
        );
    }

    //ImageContextWrapper must wrap Galeria.Image (not the <Image> inside): galeria's iOS
    //getChildImageView walks only 2 native levels for a UIImageView; nesting Zeego's
    //ContextMenuView inside Galeria.Image pushes the UIImageView to depth 4 and breaks
    //tap-to-enlarge (long-press copy still works via Zeego's own interaction).
    return (
        <ImageContextWrapper imageUri={media.uri || media.thumbnailUri}>
            <Galeria.Image index={index}>
                <Image
                    source={{ uri: media.thumbnailUri || media.uri }}
                    style={{ width: w, height: h, borderRadius: 8 }}
                    contentFit="contain"
                    transition={150}
                    onLoad={handleLoad}
                    onError={() => setErrored(true)}
                />
            </Galeria.Image>
        </ImageContextWrapper>
    );
};

const InlineVideo = ({ index, uri, thumbnailUri, width, height }) => {
    const { theme } = useUnistyles();
    const videoRef = useRef(null);
    const controlsTimerRef = useRef(null);
    const isMountedRef = useRef(true);
    const controlsOpacity = useRef(new Animated.Value(1)).current;
    const [hasStarted, setHasStarted] = useState(false);
    const [controlsInteractive, setControlsInteractive] = useState(true);
    const player = useVideoPlayer(uri, (p) => {
        p.loop = false;
    });
    const { isPlaying } = useEvent(player, 'playingChange', { isPlaying: player.playing });
    const isPlayingRef = useRef(isPlaying);
    isPlayingRef.current = isPlaying;

    const clearControlsTimer = useCallback(() => {
        if(controlsTimerRef.current){
            clearTimeout(controlsTimerRef.current);
            controlsTimerRef.current = null;
        }
    }, []);

    const hideControls = useCallback(() => {
        clearControlsTimer();
        controlsOpacity.stopAnimation();
        Animated.timing(controlsOpacity, {
            toValue: 0,
            duration: theme.animation.normal,
            useNativeDriver: Platform.OS !== 'web',
        }).start(({ finished }) => {
            if(finished && isMountedRef.current) setControlsInteractive(false);
        });
    }, [clearControlsTimer, controlsOpacity, theme.animation.normal]);

    const revealControls = useCallback(() => {
        clearControlsTimer();
        controlsOpacity.stopAnimation();
        if(isMountedRef.current) setControlsInteractive(true);
        Animated.timing(controlsOpacity, {
            toValue: 1,
            duration: theme.animation.normal,
            useNativeDriver: Platform.OS !== 'web',
        }).start();
    }, [clearControlsTimer, controlsOpacity, theme.animation.normal]);

    const scheduleControlsHide = useCallback(() => {
        clearControlsTimer();
        if(!isPlayingRef.current) return;
        controlsTimerRef.current = setTimeout(() => {
            controlsTimerRef.current = null;
            if(isPlayingRef.current) hideControls();
        }, 5000);
    }, [clearControlsTimer, hideControls]);

    const handleControlsActivity = useCallback(() => {
        if(!isPlayingRef.current) return;
        revealControls();
        scheduleControlsHide();
    }, [revealControls, scheduleControlsHide]);

    const handlePointerLeave = useCallback(() => {
        if(isPlayingRef.current) hideControls();
    }, [hideControls]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            clearControlsTimer();
            controlsOpacity.stopAnimation();
        };
    }, [clearControlsTimer, controlsOpacity]);

    useEffect(() => {
        revealControls();
        if(isPlaying){
            setHasStarted(true);
            scheduleControlsHide();
        }
    }, [isPlaying, revealControls, scheduleControlsHide]);

    const w = width || 250;
    const h = height || 250;
    const isWeb = Platform.OS === 'web';
    const controlIconColor = theme.dark ? theme.colors.text : theme.colors.surface;

    const togglePlayback = () => {
        revealControls();
        if(player.playing){
            player.pause();
            return;
        }
        setHasStarted(true);
        player.play();
    };

    const enterFullscreen = () => {
        handleControlsActivity();
        videoRef.current?.enterFullscreen();
    };

    return (
        <View
            style={styles.videoContainer(w, h)}
            onPointerEnter={isWeb ? handleControlsActivity : undefined}
            onPointerMove={isWeb ? handleControlsActivity : undefined}
            onPointerDown={isWeb ? handleControlsActivity : undefined}
            onPointerLeave={isWeb ? handlePointerLeave : undefined}
            onTouchStart={!isWeb ? handleControlsActivity : undefined}
            testID={`media-video-container-${index}`}
        >
            <VideoView
                ref={videoRef}
                player={player}
                style={styles.video}
                contentFit="contain"
                nativeControls
                fullscreenOptions={{ enable: true }}
                allowsPictureInPicture
                playsInline
                testID={`media-video-${index}`}
            />

            {!hasStarted && thumbnailUri && (
                <Image
                    source={{ uri: thumbnailUri }}
                    style={styles.videoThumbnail}
                    contentFit="contain"
                    pointerEvents="none"
                />
            )}

            <Animated.View
                style={[styles.playOverlay, { opacity: controlsOpacity }]}
                pointerEvents={controlsInteractive ? "box-none" : "none"}
                accessibilityElementsHidden={!controlsInteractive}
                importantForAccessibility={controlsInteractive ? "auto" : "no-hide-descendants"}
            >
                <Pressable
                    onPress={togglePlayback}
                    style={styles.playButton}
                    hitSlop={6}
                    accessibilityRole="button"
                    accessibilityLabel={isPlaying ? "Pause video" : "Play video"}
                    testID={`media-video-play-${index}`}
                >
                    <Unicon
                        name={isPlaying ? "pause" : "play"}
                        size={18}
                        color={controlIconColor}
                        style={!isPlaying ? styles.playIcon : undefined}
                    />
                </Pressable>
            </Animated.View>

            <Animated.View
                style={[styles.fullscreenOverlay, { opacity: controlsOpacity }]}
                pointerEvents={controlsInteractive ? "auto" : "none"}
                accessibilityElementsHidden={!controlsInteractive}
                importantForAccessibility={controlsInteractive ? "auto" : "no-hide-descendants"}
            >
                <Pressable
                    onPress={enterFullscreen}
                    style={styles.fullscreenButton}
                    accessibilityRole="button"
                    accessibilityLabel="View video fullscreen"
                    testID={`media-video-fullscreen-${index}`}
                >
                    <Unicon name="expand-outline" size={20} color={controlIconColor} />
                </Pressable>
            </Animated.View>
        </View>
    );
};

const styles = StyleSheet.create((theme) => ({
    mediaRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 4,
    },
    videoContainer: (width, height) => ({
        width,
        height,
        borderRadius: 8,
        overflow: 'hidden',
        marginVertical: 5,
        position: 'relative',
        backgroundColor: theme.colors.surfaceVariant,
    }),
    video: {
        width: '100%',
        height: '100%',
    },
    videoThumbnail: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: theme.colors.surfaceVariant,
    },
    playOverlay: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        alignItems: 'center',
        justifyContent: 'center',
    },
    playButton: {
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.dark ? theme.colors.background : theme.colors.onSurface,
        boxShadow: theme.shadows.card,
        _web: {
            cursor: 'pointer',
            _hover: { backgroundColor: theme.dark ? theme.colors.surface : theme.colors.onSurfaceVariant },
        },
    },
    playIcon: {
        marginLeft: 3,
    },
    fullscreenOverlay: {
        position: 'absolute',
        top: 8,
        right: 8,
    },
    fullscreenButton: {
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.dark ? theme.colors.background : theme.colors.onSurface,
        boxShadow: theme.shadows.card,
        _web: {
            cursor: 'pointer',
            _hover: { backgroundColor: theme.dark ? theme.colors.surface : theme.colors.onSurfaceVariant },
        },
    },
    errorPlaceholder: (width, height) => ({
        width: width,
        height: height,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        backgroundColor: theme.colors.surfaceVariant
    }),
    errorText: {
        fontSize: 12,
    },
}));

export default MediaViewer;
